import styles from "./ChatBox.module.css";
import type { useChatAttachments } from "@lib/agent/useChatAttachments";
import {
  ACCEPTED_EXTENSIONS,
  MAX_DOCS_PER_SESSION,
  admitFiles,
  notifyAdmissionWarnings
} from "@lib/agent/useChatAttachments";
import Icon from "@lib/common/components/Icon/Icon";
import ChatAttachmentChips from "@lib/experiment/agent/ChatAttachmentChips";
import type { PaperProps, TextareaProps } from "@mantine/core";
import { Button, Group, Paper, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { useField } from "@mantine/form";
import { isString } from "lodash-es";
import type { ChangeEvent, KeyboardEvent, ReactNode, Ref } from "react";
import { useCallback, useMemo, useRef } from "react";

const MAX_MESSAGE_LENGTH = 10_000;

export type ChatBoxSubmitPayload = {
  // Filenames of the attached documents, for rendering the sent message's attachment chips.
  // (Uploaded documents are attached to the session; the agent receives the full set server-side,
  // so no handle ids travel with the prompt.)
  filenames?: string[];
  // Raw files held client-side for upload by a later step (defer mode, e.g. the landing
  // page where no draft — and thus no agent session — exists yet).
  files?: File[];
};

export default function ChatBox({
  ref,
  onSubmit,
  onCancel,
  disabled = false,
  isStreaming = false,
  inputSuggestion,
  textareaProps = {},
  paperProps,
  enableAttachments = false,
  attachments,
  selectedFiles,
  onFilesChange,
  extraActions
}: {
  ref: Ref<HTMLTextAreaElement>;
  onSubmit: (message: string, payload: ChatBoxSubmitPayload) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  inputSuggestion?: string | null;
  textareaProps?: TextareaProps;
  paperProps?: Partial<PaperProps>;
  enableAttachments?: boolean;
  // Immediate-upload mode: pass the `useChatAttachments` instance bound to an experiment.
  // Picked files upload right away (attached to the session); submit just carries their filenames.
  attachments?: ReturnType<typeof useChatAttachments>;
  // Defer mode: parent owns the raw `File[]` (no upload yet) and submit forwards them.
  // Used on the landing page where the agent session doesn't exist until the draft is created.
  selectedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
  // Optional toolbar slot rendered next to the attach-document button.
  // Used by callers to inject extra actions (e.g., the research-mode button)
  // without coupling ChatBox to any specific feature.
  extraActions?: ReactNode;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const field = useField({
    initialValue: "",
    validateOnChange: true,
    validate: value => (value.length > MAX_MESSAGE_LENGTH ? "Message too long" : null)
  });
  // Destructure without error — we render our own counter instead of Mantine's default error UI
  const { value: inputValue, onChange: fieldOnChange, onBlur } = field.getInputProps();
  const isOverLimit = !!field.error;

  const isDeferMode = selectedFiles !== undefined && onFilesChange !== undefined;
  // Stabilize the fallback empty array so callbacks depending on it aren't re-created every render.
  const deferredFiles = useMemo(() => selectedFiles ?? [], [selectedFiles]);

  const hasInputSuggestion = useMemo(() => isString(inputSuggestion) && inputSuggestion.length > 0, [inputSuggestion]);

  const submit = useCallback(() => {
    const message = field.getValue().trim();
    if (!message || isOverLimit) {
      return;
    }
    // The Enter key calls submit() directly, bypassing the disabled button. Bail when the send
    // would no-op anyway — `disabled` reflects history loading/errored, and `isStreaming` means a
    // turn is in flight — so we don't clear the typed text and uploaded attachments without sending.
    if (disabled || isStreaming) {
      return;
    }
    // Guard here (not only via the disabled button): the Enter key calls submit() directly, and
    // sending mid-upload would drop the still-uploading file's handle from the prompt.
    if (attachments?.hasPendingUploads) {
      return;
    }
    onSubmit(message, {
      filenames: attachments
        ? attachments.items.filter(i => i.status === "uploaded").map(i => i.filename)
        : isDeferMode
          ? deferredFiles.map(f => f.name)
          : undefined,
      files: isDeferMode ? deferredFiles : undefined
    });
    field.setValue("");
    attachments?.reset();
    if (isDeferMode) {
      onFilesChange([]);
    }
  }, [field, isOverLimit, onSubmit, attachments, isDeferMode, deferredFiles, onFilesChange, disabled, isStreaming]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !isStreaming) {
        e.preventDefault();
        submit();
      } else if (e.key === "Tab" && hasInputSuggestion && field.getValue() === "") {
        e.preventDefault();
        // Auto-insert inputSuggestion on Tab.
        field.setValue((inputSuggestion ?? "").replace("e.g. ", ""));
        e.currentTarget.focus();
      }
    },
    [isStreaming, submit, hasInputSuggestion, field, inputSuggestion]
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const incoming = Array.from(e.target.files ?? []);
      // Reset the input so the same file can be re-picked after removal.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (incoming.length === 0) {
        return;
      }

      if (attachments) {
        attachments.addFiles(incoming);
        return;
      }

      // Defer mode: mirror the cap/size checks locally since there's no upload hook yet.
      const admission = admitFiles(incoming, { existingCount: deferredFiles.length });
      notifyAdmissionWarnings(admission);
      if (admission.accepted.length > 0 && onFilesChange) {
        onFilesChange([...deferredFiles, ...admission.accepted]);
      }
    },
    [attachments, deferredFiles, onFilesChange]
  );

  const removeDeferredFile = useCallback(
    (key: string) => {
      const index = Number(key);
      if (onFilesChange) {
        onFilesChange(deferredFiles.filter((_, i) => i !== index));
      }
    },
    [deferredFiles, onFilesChange]
  );

  const chipItems = attachments
    ? attachments.items.map(item => ({ key: item.localId, filename: item.filename, status: item.status }))
    : deferredFiles.map((file, index) => ({ key: String(index), filename: file.name }));

  const remainingSlots = attachments ? attachments.remainingSlots : MAX_DOCS_PER_SESSION - deferredFiles.length;
  // Attaching stays available while the agent is replying: the file uploads to the session right
  // away and rides the NEXT message (only sending is blocked mid-reply). This also keeps the
  // tooltip honest — its "limit reached" state now only shows at the actual limit.
  const canAttachMore = remainingSlots > 0;
  const submitDisabled = disabled || !inputValue.trim() || isOverLimit || Boolean(attachments?.hasPendingUploads);
  const showCounter = inputValue.length >= MAX_MESSAGE_LENGTH - 500;
  const placeholder = useMemo(() => {
    if (hasInputSuggestion) {
      return inputSuggestion + "  (press Tab to use suggestion)";
    }
    return textareaProps.placeholder ?? "";
  }, [hasInputSuggestion, inputSuggestion, textareaProps.placeholder]);

  return (
    <Paper
      classNames={{ root: styles.textareaPaperWrapper }}
      shadow="xxs"
      px="sm"
      pt={5}
      pb="xs"
      withBorder
      w="100%"
      radius="md"
      {...paperProps}
    >
      <Stack gap="xs" align="space-between" h="100%">
        {enableAttachments && (
          // Uploaded documents can't be removed individually (only clearing the chat deletes
          // them), so chips in immediate-upload mode have no remove button. Defer-mode files
          // aren't uploaded yet — removing one only edits the local selection.
          <ChatAttachmentChips items={chipItems} onRemove={attachments ? undefined : removeDeferredFile} />
        )}
        <Textarea
          ref={ref}
          {...textareaProps}
          placeholder={placeholder}
          value={inputValue}
          // Compose handlers to ensure both internal state and any external
          // onChange (e.g., typewriter animation control) are called
          onChange={e => {
            fieldOnChange(e);
            textareaProps.onChange?.(e);
          }}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          size="md"
          autoFocus
          data-autofocus
          autosize={textareaProps.autosize ?? true}
          minRows={textareaProps.minRows ?? 1}
          maxRows={textareaProps.maxRows ?? 6}
          variant="unstyled"
          radius="md"
          fw={500}
        />

        <Group justify="space-between" align="flex-end">
          <Group gap="xs">
            {enableAttachments && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <Tooltip
                  label={
                    canAttachMore
                      ? "Attach papers, datasheets, or any relevant document"
                      : `Document limit reached (${MAX_DOCS_PER_SESSION}/session)`
                  }
                >
                  <Button
                    color="gray"
                    variant="subtle"
                    size="sm"
                    miw="unset"
                    px="xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canAttachMore}
                  >
                    <Icon name="paperclip" />
                  </Button>
                </Tooltip>
              </>
            )}
            {extraActions}
          </Group>
          <Group gap="xs" align="flex-end">
            {showCounter && (
              <Text size="xs" c={isOverLimit ? "red" : "dimmed"}>
                {inputValue.length.toLocaleString()}/{MAX_MESSAGE_LENGTH.toLocaleString()}
              </Text>
            )}
            {isStreaming ? (
              <Button color="gray" variant="light" size="xs" miw="unset" px="xs" onClick={onCancel}>
                <Icon name="stop" />
              </Button>
            ) : (
              <Button
                color="var(--mantine-primary-color-filled)"
                variant="filled"
                size="xs"
                miw="unset"
                px="xs"
                disabled={submitDisabled}
                onClick={submit}
              >
                <Icon name="arrow-up" />
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}
